<template>
  <nav class="fu-tabs" aria-label="Follow-up pages" data-test="fu-tabs">
    <div
      ref="list"
      class="fu-tabs__list"
      :class="{ 'fu-tabs__list--more-r': moreRight, 'fu-tabs__list--more-l': moreLeft }"
      :data-overflow="moreRight || moreLeft ? 'true' : null"
      @scroll="measureOverflow"
    >
      <router-link
        v-for="t in tabs"
        :key="t.key"
        :to="t.to"
        class="fu-tab"
        :class="{ 'fu-tab--on': t.key === active }"
        :aria-current="t.key === active ? 'page' : null"
        :data-test="`fu-tab-${t.key}`"
      >
        {{ t.label }}<span
          v-if="hasCount(t)"
          class="fu-tab__count"
          :aria-label="`${t.count} due now`"
          :data-test="`fu-tab-count-${t.key}`"
        >{{ t.count }}</span>
      </router-link>
    </div>
    <span v-if="anyCount" class="fu-tabs__note">Counts = follow-ups due now</span>
  </nav>
</template>

<script>
/**
 * The tab bar across the four follow-up pages (the export's lines 111–124). Each tab is a
 * <router-link> to the page's EXISTING route — the page passes the list in (pageChrome.js
 * visibleTabs, which also drops the tabs this user may not see, by the header's rule).
 *
 * A count shows only when the tab has a number: pages pass one only for rows they already have
 * loaded, never fetched to fill a badge. "Counts = follow-ups due now" shows when any tab has one
 * (desktop only — the export hides it below 900px).
 *
 *   tabs   [{ key, label, to, count: number|null }]
 *   active the current page's key
 *
 * Narrow screens (polish 2026-10-03 — on a phone "Unscheduled treatment" was cut at the edge with
 * nothing saying the bar scrolls): the active tab is scrolled into view inside the bar (the bar
 * only — never the page), and an edge that has more tabs past it fades out.
 */
export default {
  name: 'FollowupsTabs',
  props: {
    tabs: { type: Array, default: () => [] },
    active: { type: String, default: '' },
  },
  data() {
    return { moreLeft: false, moreRight: false };
  },
  computed: {
    anyCount() {
      return this.tabs.some(t => this.hasCount(t));
    },
  },
  watch: {
    active() { this.$nextTick(this.revealActive); },
    tabs() { this.$nextTick(this.measureOverflow); },
  },
  mounted() {
    this.revealActive();
    window.addEventListener('resize', this.measureOverflow);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.measureOverflow);
  },
  methods: {
    hasCount(t) {
      return typeof t.count === 'number' && Number.isFinite(t.count);
    },
    measureOverflow() {
      const el = this.$refs.list;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      this.moreLeft = max > 1 && el.scrollLeft > 1;
      this.moreRight = max > 1 && el.scrollLeft < max - 1;
    },
    // Sets the bar's scrollLeft directly (scrollIntoView would also scroll the page).
    revealActive() {
      const el = this.$refs.list;
      const tab = el && el.querySelector('.fu-tab--on');
      if (el && tab && el.scrollWidth > el.clientWidth) {
        const pad = 24; // leaves the fade off the active tab
        const left = tab.offsetLeft; // the list is position: relative, so this is inside the list
        const right = left + tab.offsetWidth;
        if (left - pad < el.scrollLeft) el.scrollLeft = Math.max(0, left - pad);
        else if (right + pad > el.scrollLeft + el.clientWidth) el.scrollLeft = right + pad - el.clientWidth;
      }
      this.measureOverflow();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-tabs {
  display: flex;
  align-items: center;
  gap: 28px;
  margin-bottom: 22px;
  border-bottom: 1px solid $fu-border;
  min-width: 0;
}

// The tabs scroll sideways on a narrow screen rather than wrap onto a second line.
.fu-tabs__list {
  position: relative;
  display: flex;
  align-items: center;
  gap: 28px;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}

// An edge with more tabs past it fades out, so a cut-off tab reads as "scroll for more".
.fu-tabs__list--more-r {
  -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 40px), transparent);
  mask-image: linear-gradient(to right, #000 calc(100% - 40px), transparent);
}
.fu-tabs__list--more-l {
  -webkit-mask-image: linear-gradient(to left, #000 calc(100% - 40px), transparent);
  mask-image: linear-gradient(to left, #000 calc(100% - 40px), transparent);
}
.fu-tabs__list--more-l.fu-tabs__list--more-r {
  -webkit-mask-image: linear-gradient(to right, transparent, #000 40px, #000 calc(100% - 40px), transparent);
  mask-image: linear-gradient(to right, transparent, #000 40px, #000 calc(100% - 40px), transparent);
}

.fu-tab {
  @include fu-focus-ring;
  display: inline-flex;
  align-items: center;
  gap: $fu-space-2;
  padding: 0 2px 12px;
  white-space: nowrap;
  font-size: 14.5px;
  font-weight: $fu-fw-bold;
  color: $fu-text-subtle;
  text-decoration: none;
  border-radius: $fu-radius-xs $fu-radius-xs 0 0;
  &:hover { color: $fu-neutral-700; text-decoration: none; }
}

.fu-tab--on {
  color: $fu-text;
  box-shadow: inset 0 -2px 0 $fu-brand;
  &:hover { color: $fu-text; }
}

.fu-tab__count {
  @include fu-tabular;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 7px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: $fu-fw-bold;
  background: $fu-neutral-100;
  color: $fu-neutral-600;
}

.fu-tab--on .fu-tab__count {
  background: $fu-brand;
  color: #ffffff;
}

.fu-tabs__note {
  margin-left: auto;
  padding-bottom: 12px;
  font-size: 12.5px;
  color: $fu-text-subtle;
  white-space: nowrap;
}

@include fu-tablet {
  .fu-tabs__note { display: none; }
}

@include fu-phone {
  .fu-tabs, .fu-tabs__list { gap: $fu-space-5; }
}
</style>
